import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, XCircle, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "jotbay_dunning_dismissed";

export default function DunningBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  const suspended = user?.payment_suspended === true;
  const pastDue = user?.payment_past_due === true && !suspended;
  if (!suspended && !pastDue) return null;

  const canManageBilling =
    user?.account_type !== "organization" ||
    user?.is_primary_admin === true ||
    user?.is_org_admin === true;

  // The at-risk (past-due) banner is only actionable by billing-capable users; don't alarm
  // regular members who can't fix it. (The suspended banner keeps its existing behaviour.)
  if (pastDue && !canManageBilling) return null;
  if (pastDue && dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  let retryLine = null;
  if (pastDue && user?.payment_retry_at) {
    const d = new Date(user.payment_retry_at);
    if (!isNaN(d.getTime())) {
      retryLine = t("dunning.retryOn", {
        date: d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
      });
    }
  }

  // Suspended => red (implies termination). Past-due => amber (action needed, not terminal).
  const styles = suspended
    ? "border-red-300 bg-red-50 text-red-800"
    : "border-amber-300 bg-amber-50 text-amber-900";
  const btnStyles = suspended
    ? "bg-red-700 hover:bg-red-800 text-white"
    : "bg-amber-600 hover:bg-amber-700 text-white";
  const Icon = suspended ? XCircle : AlertTriangle;

  const title = suspended ? t("dunning.suspendedTitle") : null;
  const message = suspended
    ? (canManageBilling ? t("dunning.suspendedMsg") : t("dunning.suspendedAdminMsg"))
    : t("dunning.atRiskMessage");
  const cta = suspended ? t("dunning.reactivateCta") : t("dunning.updateBillingCta");

  return (
    <div
      className={`mb-6 rounded-xl border px-4 py-3.5 ${styles}`}
      data-testid={suspended ? "suspension-banner" : "dunning-banner"}
    >
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          {title && <p className="font-medium text-sm">{title}</p>}
          <p className={`text-sm leading-relaxed ${title ? "text-xs mt-0.5 opacity-90" : "font-medium"}`}>{message}</p>
          {retryLine && (
            <p className="text-xs mt-1 font-medium" data-testid="dunning-retry-line">{retryLine}</p>
          )}
        </div>
        {canManageBilling && (
          <Button
            asChild
            size="sm"
            className={`h-8 text-xs shrink-0 ${btnStyles}`}
            data-testid={suspended ? "reactivate-btn" : "dunning-update-btn"}
          >
            <Link to="/app/billing">{cta}</Link>
          </Button>
        )}
        {pastDue && (
          <button
            onClick={dismiss}
            aria-label={t("dunning.dismiss")}
            data-testid="dunning-dismiss-btn"
            className="shrink-0 text-amber-700 hover:text-amber-900 p-1 rounded-md hover:bg-amber-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
